import {
  Button,
  MenuItem,
  MenuList,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { type ChangeEvent, useMemo, useState } from 'react'
import { useDebounce } from 'react-use'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import useAuth from 'hooks/use-auth'
import { muiTheme } from 'mui-theme'
import { naturalSortBy } from 'utils/list'

import type { UserSessionTeam } from 'types/api/users/sessions/user-session'

export const TeamSelectionMenuPanel: React.FC<{
  onHideTeamSelectionMenuPanel(): void
  onSelectActiveTeam(Team: UserSessionTeam): void
}> = ({ onHideTeamSelectionMenuPanel, onSelectActiveTeam }) => {
  const { session } = useAuth()

  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')

  const handleChangeSearchValue = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue)
    },
    300,
    [searchValue]
  )

  const filteredTeams = useMemo(() => {
    const allTeams = session?.teams ?? []

    return naturalSortBy(
      allTeams.filter((t) =>
        t.name.toLowerCase().includes(debouncedSearchValue.trim().toLowerCase())
      ),
      (t) => t.name
    )
  }, [debouncedSearchValue, session?.teams])

  return (
    <Stack
      data-testid="team-selection-menu-panel"
      flexGrow={1}
      gridTemplateRows="auto auto 1fr"
      spacing={2}
      sx={{ overflowY: 'hidden' }}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={2}
      >
        <Button
          aria-label="Back"
          data-testid="team-selection-back-button"
          startIcon={<Icon icon="chevronLeft" />}
          onClick={onHideTeamSelectionMenuPanel}
        >
          Back
        </Button>
        <Typography>Switch Team</Typography>
      </Stack>
      <TextField
        aria-label="Search Teams"
        data-testid="team-selection-search-input"
        label="Search Teams"
        value={searchValue}
        variant="outlined"
        onChange={handleChangeSearchValue}
      />
      <MenuList
        data-testid="team-selection-menu-list"
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          flexGrow: 1,
          overflowY: 'scroll'
        }}
      >
        {filteredTeams.map((team) => (
          <MenuItem
            key={team.identifier}
            divider
            data-testid={`team-selection-menu-list-item-${team.identifier}`}
            onClick={() => onSelectActiveTeam(team)}
          >
            <Stack
              alignItems="center"
              direction="row"
              spacing={2}
              sx={{ py: 1 }}
              width="100%" // Necessary to truncate the team name text
            >
              <Typography color={muiTheme.palette.text.primary}>
                <Icon icon="solidUserGroup" />
              </Typography>
              <TruncatedTextWithTooltip tooltipProps={{ title: team.name }}>
                {team.name}
              </TruncatedTextWithTooltip>
            </Stack>
          </MenuItem>
        ))}
      </MenuList>
    </Stack>
  )
}
